// React core
import React, { useState, useEffect } from 'react';

// Router utilities
import { useLocation } from 'react-router-dom';

// Icons
import { IoSend } from 'react-icons/io5';
import { MdOutlineAddBox, MdOutlineKeyboardArrowRight, MdWarningAmber } from 'react-icons/md';
import { IoMdClose } from 'react-icons/io';
import { CgClose } from 'react-icons/cg';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { Copy, CopyCheck, RefreshCw, ThumbsDown, ThumbsUp, X } from 'lucide-react';

// Third-party UI helpers
import { toast } from 'react-toastify';
import Linkify from 'react-linkify';

// Global shared components
import ThreeDotLoader from '../../../components/ThreeDotLoader';
import ScrollToBottom from '../../../components/ScrollToBottom';
import CalendlyEmbed from '../../../components/CalendlyEmbed';

// API helpers
import { getWithToken, getWithoutToken, postWithoutToken, deleteWithoutToken, deleteAllWithoutToken, getLocalStorage } from '../../../API/Api';

// Global constants
import { formatTimeAgo } from '../../../API/Constant';

// Global utilities
import { getChatbotPositionStyle } from '../../../assets/Helper/utils';

// Widget-specific constants
import { DEFAULT_IMAGES, INITIAL_FORM_VALUE, INITIAL_WELCOME_DETAILS } from './constants/index';

// Widget-specific utilities
import { findCorrespondingPrompt, findPromptIndex, formatText, generateSessionId, getIdsToDelete, isMobileDevice, MessageComponent } from './utils';
import { ChatboxFooter } from './components/ChatboxFooter';
import { DeleteConfirmationModal } from './components/DeleteConfirmationModal';
import { PublishWarningModal } from './components/PublishWarningModal';
import { LeadButtons } from './components/LeadButtons';
import { ChatInput } from './components/ChatInput';
import { LeadForm } from './components/LeadForm';
import { Recommendations } from './components/Recommendations';

const WidgetChatbot = (props) => {
  const isMobile = isMobileDevice();
  const [chatData, setChatData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [values, setValues] = useState('');
  const [widgetDetails, setWidgetDetails] = useState('');
  const [openChatbox, setOpenChatbox] = useState(false);
  const [companyID, setCompanyID] = useState('');
  const widgetData = useLocation();
  const [sessionId, setSessionId] = useState('');
  const [hideButtons, setHideButtons] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const [customDisabled, setCustomDisabled] = useState(false);
  const [welBubblePopup, setWelBubblePopup] = useState(false);
  const [deleteConfirmPopup, setDeleteConfirmPopup] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isLeadButtonHide, setIsLeadButtonHide] = useState(false);
  const [showPublishWarning, setShowPublishWarning] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showCalendar, setShowCalendar] = useState({
    show: false,
    link: '',
  });
  const [showFormButton, setShowFormButton] = useState(false);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [leadGenerate, setLeadGenerate] = useState(false);
  const [ip, setIp] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const paramsCompanyID = params.get('company_id');
  const paramsleadGenerated = params.get('leadGenerated');
  const paramsID = params.get('id');
  const paramsPreData = params.get('previewData');
  const [welcomeDetails, setWelcomeDetails] = useState(INITIAL_WELCOME_DETAILS);
  const [feedbackState, setFeedbackState] = useState({});
  const [showPrivacy, setShowPrivacy] = useState(true);

  useEffect(() => {
    fetch('https://api.ipify.org/?format=json')
      .then((res) => res.json())
      .then((data) => setIp(data?.ip))
      .catch((err) => console.error('Error fetching IP:', err));
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const getBotDetails = (token) => {
    getWithToken('company/', token)
      .then((response) => {
        if (response.status === 201) {
          setCompanyID(response?.data?.id);
          getWelcomeMsg(response?.data?.id);
        }
      })
      .catch((error) => {
        console.error('error: ', error);
      });
  };

  useEffect(() => {
    let token = getLocalStorage('apiToken');
    if (token) {
      getBotDetails(JSON.parse(token));
    }
  }, [chatData]);

  useEffect(() => {
    if (paramsID || props.widgetID) {
      fetchWidgetDetails(paramsID || props.widgetID);
    }
  }, [props?.refresh, paramsID, props.widgetID]);

  useEffect(() => {
    setSessionId(generateSessionId());
  }, []);

  useEffect(() => {
    if (widgetDetails?.buble_timer_count && widgetDetails?.buble_active_status) {
      if (widgetDetails.status && widgetDetails.buble_active_status) {
        if (!paramsleadGenerated && !widgetDetails?.popup_status) {
          setTimeout(() => {
            setWelBubblePopup(true);
          }, widgetDetails?.buble_timer_count * 1000);
        }
      }
    }
  }, [widgetDetails, paramsleadGenerated]);

  const fetchWidgetDetails = (id) => {
    if (paramsPreData || props.widgetID) {
      getWithoutToken(`Webchat_test_preview_widgetAPI/${id}`)
        .then((response) => {
          if (response.status === 200) {
            const widgetData = response?.data;
            setWidgetDetails({ ...widgetData });
            getWelcomeMsg(widgetData?.company);

            if (paramsleadGenerated) {
              setLeadGenerate(true);
              setOpenChatbox(true);
              return;
            }
            if (widgetData?.status && widgetData?.popup_status) {
              const delay = widgetData?.timer_count * 1000;
              setTimeout(() => {
                if (window.location?.pathname === '/preview') {
                  setWelBubblePopup(false);
                  setOpenChatbox(true);
                }
              }, delay);
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching widget details:', error);
        });
    }
  };

  const getShowRecommendation = (id) => {
    getWithoutToken(`lead_answers_retrieve/${id}`)
      .then((response) => {
        if (response.status === 200) {
          setShowRecommendation(response?.data);
        }
      })
      .catch((error) => {
        console.error('error: ', error);
        toast.error('Something went wrong');
      });
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (values.trim().length > 0) {
      let newObj = { prmt: values };
      chatData.push(newObj);
      let body = {
        prompt: values,
        session_id: sessionId,
        ip_address: ip,
      };
      setValues('');
      setLoader(true);
      setIsButtonDisabled(true);
      setShowRecommendation(null);
      setShowCalendar({});
      chatPostApi(body);
    } else {
      toast.dismiss();
      toast.error('Message is blank.');
      setLoader(false);
    }
  };

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setChatData((prev) => [...prev]);
  //   }, 60000);
  //   return () => clearInterval(interval);
  // }, []);

  const chatPostApi = async (body, retryIndex = null) => {
    try {
      setCustomDisabled(true);
      setShowPrivacy(false);
      const companyIdToUse = companyID || paramsCompanyID;

      const response = await postWithoutToken('LoginChatGPTAPI?company=' + companyIdToUse, body);

      if (response.status === 200) {
        setChatData((prev) => {
          let updated = [...prev];
          response.data.forEach(({ text, id, lead_form, calendar, recommendation }) => {
            const tempData = { text, id, timestamp: new Date() };
            if (retryIndex !== null) {
              updated.push(tempData);
            } else {
              updated.push(tempData);
            }
            if (!welcomeDetails?.lead_choice) setHideButtons(false);
            if (lead_form) setShowForm(true);
            if (calendar)
              setShowCalendar({
                show: true,
                link: calendar,
              });
            if (recommendation) getShowRecommendation(recommendation);
          });

          return updated;
        });
      } else if (response.status === 400) {
        if (response.message === 'Just before you test me, first click the Publish Chatbot button at the top right of the page.') {
          setOpenChatbox(false);
          setShowPublishWarning(true);
          handleClearData();
        } else {
          toast.error(response.message);
        }
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error(error);
      toast.error('An unexpected error occurred.');
    } finally {
      setCustomDisabled(false);
      setIsButtonDisabled(false);
      setLoader(false);
    }
  };
  const handleChoice = (e, Data) => {
    setHideButtons(true);
    setIsButtonDisabled(true);
    let newObj = { prmt: Data };
    chatData.push(newObj);
    let body = {
      prompt: Data,
      session_id: sessionId,
      ip_address: ip,
    };
    setLoader(true);
    setOpenChatbox(true);
    chatPostApi(body);
  };

  const getWelcomeMsg = (companyID) => {
    var companyIdToUse = companyID || paramsCompanyID;
    getWithoutToken('welcomesms_linkAPI/?company=' + companyIdToUse)
      .then((response) => {
        if (response.status === 200) {
          setWelcomeDetails({
            welcome_msg: response?.data?.welcome_msg,
            lead_choice: response?.data?.lead_choice,
            lead_status: response?.data?.status,
            generate_lead_choice: response?.data?.generate_lead_choice,
            create_lead_choice: response?.data?.create_lead_choice,
            is_calendar: response?.data?.is_calendar,
            lead_form: response?.data?.lead_form,
            is_powered_by_visible: response?.data?.is_powered_by_visible,
          });
        }
      })
      .catch((error) => {
        toast.error('Something went wrong');
      });
  };

  const handleFormSubmit = (formData) => {
    setIsButtonDisabled(true);
    var companyIdToUse = companyID || paramsCompanyID;
    const payload = {
      ...formData,
      recipient_id: sessionId,
      ip_address: ip,
    };
    postWithoutToken('Create_LeadInformationAPI?company=' + companyIdToUse, payload)
      .then((response) => {
        if (response.status === 201) {
          setShowForm(false);
          const formattedPrompt = `**Name:** ${formData.name}\n**Email:** ${formData.email}\n**Number:** ${formData.phone_number}`;

          handleChoice(null, formattedPrompt);
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error('error: ', error);
        toast.error('Something went wrong');
        setIsButtonDisabled(false);
      });
  };

  const handleClearData = () => {
    var companyIdToUse = companyID || paramsCompanyID;
    deleteWithoutToken('LoginChatGPTAPI?company=' + companyIdToUse)
      .then((response) => {
        if (response.status === 200) {
          setChatData([]);
          setValues('');
          setHideButtons(false);
          setShowRecommendation(null);
          setShowFormButton(false);
          setWelBubblePopup(false);
          setShowForm(false);
          setIsLeadButtonHide(false);
          setIsButtonDisabled(false);
          setDeleteConfirmPopup(false);
          setIsDropdownOpen(false);
          setShowPrivacy(true);
          setSessionId(generateSessionId());
          setShowCalendar({});
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error('error: ', error);
        toast.error('Something went wrong');
      });
  };

  const fetchCalendlyLink = async () => {
    try {
      const companyIdToUse = companyID || paramsCompanyID;
      const url = `create_calendly_scheduling_link_withouttoken/?company=${companyIdToUse}`;

      const response = await postWithoutToken(url, { session_id: sessionId });

      if (response?.status === 200 && response?.data) {
        return response.data;
      }

      throw new Error(response?.message || 'Unable to generate Calendly link');
    } catch (error) {
      throw error;
    }
  };

  // Calendly Link Handler
  const handleGenerateLead = async () => {
    try {
      setIsLeadButtonHide(true);

      if (welcomeDetails?.lead_form) {
        setShowForm(true);
        return;
      }

      // Fetch Calendly link
      const calendlyLink = await fetchCalendlyLink();

      setShowCalendar({
        show: true,
        link: calendlyLink,
      });
    } catch (error) {
      toast.error(error?.message || 'Something went wrong');
      setIsLeadButtonHide(false);
    }
  };

  const handleCopyMessage = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast.error('Failed to copy message');
    }
  };

  const handleRetryMessage = async (prompt, messageIndex) => {
    console.log('prompt: ', prompt);
    if (!prompt) return;
    const botMessageIndex = messageIndex + 1;

    const idsToDelete = getIdsToDelete(chatData, botMessageIndex);

    // Call delete API first
    if (idsToDelete.length > 0) {
      try {
        await deleteAllWithoutToken('DeleteChatMessages', { ids: idsToDelete });
      } catch (err) {
        console.error('Delete API failed:', err);
      }
    }

    setChatData((prev) => {
      let updated = [...prev];

      if (botMessageIndex === prev.length - 1) {
        updated.splice(botMessageIndex, 1);
      } else {
        updated = updated.slice(0, botMessageIndex);
      }
      return updated;
    });
    // Prepare API body
    const body = {
      prompt,
      session_id: sessionId,
      ip_address: ip,
    };

    setLoader(true);
    setIsButtonDisabled(true);
    setCustomDisabled(true);
    setShowRecommendation(null);
    setShowCalendar({});

    chatPostApi(body, messageIndex);
  };

  // Single unified function for both positive and negative feedback
  const handleFeedback = async (messageId, ratingType) => {
    try {
      const companyIdToUse = companyID || paramsCompanyID;
      const response = await postWithoutToken('MessageRating?company=' + companyIdToUse, {
        chat_id: messageId,
        rating_type: ratingType,
      });

      if (response) {
        setFeedbackState((prev) => ({ ...prev, [messageId]: ratingType }));
      }
    } catch (error) {
      console.error(`Error submitting ${ratingType} feedback:`, error);
      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  // Chatbox Container Style
  const chatboxStyle = {
    background: widgetDetails?.background_color?.includes('http') ? `url(${widgetDetails?.background_color})` : widgetDetails?.background_color,
    backgroundSize: 'cover',
    transition: !leadGenerate ? '0.5s all' : '',
    width: `${widgetDetails?.chatbot_slider_size}px`,
    transform: openChatbox ? 'translateX(0)' : `translateX(${Number(widgetDetails?.chatbot_slider_size) + 50}px)`,
  };

  return (
    <>
      {widgetData?.pathname === '/preview' && (
        <div className='widgetChabot-iframe h-100'>
          <iframe src={widgetDetails?.website} title='Tutorials'></iframe>
        </div>
      )}
      <div className={`chatbox-container ${openChatbox ? (leadGenerate ? 'widgetLeadOpen' : 'widgetOpen') : ''}`} style={chatboxStyle}>
        <div className='chatbox-header flex-column justify-content-center'>
          {!paramsleadGenerated && (
            <div
              className='close-arrow'
              style={{ background: widgetDetails?.client_bubble_color }}
              onClick={() => {
                setOpenChatbox(false);
                setWelBubblePopup(false);
                setDeleteConfirmPopup(false);
                setLeadGenerate(false);
              }}
            >
              <MdOutlineKeyboardArrowRight style={{ color: widgetDetails?.bubble_text_color }} />
            </div>
          )}

          {/* Add Three Dot Dropdown */}
          <div className='chatbox-dropdown-container'>
            <div className='chatbox-three-dots' onClick={toggleDropdown}>
              <BsThreeDotsVertical style={{ color: widgetDetails?.client_bubble_color || '#000000ff', fontSize: '20px' }} />
            </div>

            {isDropdownOpen && (
              <div className='chatbox-dropdown-menu'>
                <div className='dropdown-item' onClick={(e) => handleClearData(e)}>
                  <MdOutlineAddBox style={{ color: widgetDetails?.client_bubble_color, fontSize: '16px' }} />
                  <span className='outfit-regular'>Start a new chat</span>
                </div>
                <div
                  className='dropdown-item'
                  onClick={() => {
                    if (chatData?.length > 0) {
                      setIsDropdownOpen(false);
                      setDeleteConfirmPopup(true);
                    }
                  }}
                  style={{
                    cursor: chatData?.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: chatData?.length === 0 ? 0.5 : 1,
                  }}
                >
                  <CgClose style={{ color: widgetDetails?.client_bubble_color, fontSize: '16px' }} />
                  <span className='outfit-regular'>End chat</span>
                </div>
              </div>
            )}
          </div>

          <div
            className='chatbox-header-icon'
            style={{
              maxWidth: widgetDetails?.header_logo_size ? `${widgetDetails.header_logo_size}px` : '200px',
              width: '100%',
              display: 'inline-block',
            }}
          >
            <img src={widgetDetails?.chatbot_logo || DEFAULT_IMAGES.COMNIQ_LOGO} alt='chatbox-logo' className='w-100' style={{ objectFit: 'contain', display: 'block' }} />
          </div>
          {/* <div className='chatbox-header-text mt-2'>
            <p className='outfit-bold' style={{ color: widgetDetails?.heading_font_color, fontSize: `${widgetDetails?.heading_font_size}px` }}>
              {widgetDetails?.heading}
            </p>
          </div> */}

          {/* Overlay to close dropdown when clicking outside */}
          {isDropdownOpen && <div className='dropdown-overlay' onClick={() => setIsDropdownOpen(false)} />}
        </div>
        <div className='custom-hr m-0' style={{ borderTop: `2px solid ${widgetDetails?.client_bubble_color}` }}></div>
        <div className='chatbox-body pb-0'>
          <div className={`chatbox-body-box ${!hideButtons && welcomeDetails?.lead_choice !== null && 'pt-5 mb-0'}`}>
            <div className='w-100'>
              {welcomeDetails?.welcome_msg !== null && (
                <>
                  {welcomeDetails?.lead_status === true ? (
                    <div
                      className={`chatbot-welcome-body ${hideButtons && welcomeDetails?.lead_choice !== null ? 'p-0' : 'chatbot-withBoxShadow'}`}
                      style={{
                        background: hideButtons && welcomeDetails?.lead_choice !== null ? 'initial' : widgetDetails?.inside_bubble_bg_color,
                        width: `${widgetDetails?.inside_bubble_width}px`,
                        height: hideButtons && welcomeDetails?.lead_choice !== null ? '0px' : `${widgetDetails?.inside_bubble_height}px`,
                        margin: hideButtons && welcomeDetails?.lead_choice !== null ? '0' : '0 auto',
                      }}
                    >
                      {!hideButtons && (
                        <>
                          {!showForm && (
                            <div
                              className='d-flex justify-content-center align-items-center chatbot-popup-icon'
                              style={{
                                width: `${widgetDetails?.bubble_avatar_size}px`,
                                height: `${widgetDetails?.bubble_avatar_size}px`,
                                minWidth: `${widgetDetails?.bubble_avatar_size}px`,
                                minHeight: `${widgetDetails?.bubble_avatar_size}px`,
                                borderRadius: '50%',
                                backgroundColor: widgetDetails?.inside_bubble_bg_color,
                                position: 'absolute',
                                top: 0,
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                              }}
                            >
                              <img
                                src={widgetDetails?.chatbot_avtar || DEFAULT_IMAGES.SIDEBAR_BOX_IMG}
                                alt='chatbot-icon'
                                style={{ width: '90%', height: 'auto', objectFit: 'cover' }}
                              />
                            </div>
                          )}
                          <div className={`d-flex align-items-center ${showForm && 'mt-3'}`}>
                            {showForm && (
                              <div className='chatbox-user-icon me-3'>
                                <img src={widgetDetails?.chatbot_avtar || DEFAULT_IMAGES.SIDEBAR_BOX_IMG} alt='chatbot-icon' />
                              </div>
                            )}
                            <div className={`${showForm ? 'chatbox-user-replytext' : 'mt-4'}`} style={{ background: showForm && widgetDetails?.color }}>
                              <p
                                className='m-0 outfit-bold text-center'
                                style={{ color: widgetDetails?.inside_text_color_bubble, fontSize: `${widgetDetails?.inside_bubble_text_size}px` }}
                              >
                                {welcomeDetails?.welcome_msg}
                              </p>
                            </div>
                          </div>
                        </>
                      )}
                      {!hideButtons && welcomeDetails?.lead_choice != null && (
                        <div className='d-flex flex-column align-items-center gap-3 w-100'>
                          {welcomeDetails?.lead_choice.map((choice, index) => (
                            <React.Fragment key={index}>
                              <button
                                className='btn btn-submit-bubble outfit-regular'
                                style={{
                                  marginTop: index === 0 ? '1rem' : '0',
                                  color: index === 0 ? widgetDetails?.inside_btn_primary_text : widgetDetails?.inside_btn_secondary_text,
                                  background: index === 0 ? widgetDetails?.inside_btn_primary_bg : widgetDetails?.inside_btn_secondary_bg,
                                  fontSize: index === 0 ? `${widgetDetails?.user_bubble_font_size}px` : `${widgetDetails?.agent_bubble_font_size}px`,
                                  // border: `2px solid ${widgetDetails?.client_bubble_color}`,
                                  width: `${widgetDetails?.inside_bubble_button_width}px`,
                                  height: `${widgetDetails?.inside_bubble_button_height}px`,
                                  borderRadius: '34px',
                                }}
                                onClick={
                                  index === 0
                                    ? (e) => handleChoice(e, choice)
                                    : (e) => {
                                        handleChoice(e, choice);
                                        setShowFormButton(true);
                                      }
                                }
                              >
                                {choice}
                              </button>

                              <style>
                                {` 
                                   
                                   .hr-line::after {                     
                                     background-color: ${widgetDetails?.text_color};
                                   }
                                   .hr-line::before {                      
                                     background-color: ${widgetDetails?.text_color};
                                   }

                                   .chat-group-btn:hover {
                                     background-color: ${widgetDetails?.client_bubble_color};
                                     color: ${widgetDetails?.bubble_text_color}; 
}
                                 `}
                              </style>
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className='d-flex align-items-center mt-3'>
                      <div className='custom-chat-anstext' style={{ background: widgetDetails?.color }}>
                        <div className='chatbox-user-icon mb-2'>
                          <img src={widgetDetails?.chatbot_avtar || DEFAULT_IMAGES.SIDEBAR_BOX_IMG} alt='chatbot-icon' />
                        </div>
                        <p className='m-0' style={{ color: widgetDetails?.agent_bubble_text_color }}>
                          {welcomeDetails?.welcome_msg}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
              {chatData.length > 0 &&
                chatData?.map((obj, index) => {
                  const messageId = obj.id || obj.chat_id;
                  const currentFeedback = feedbackState[messageId];
                  return (
                    <>
                      {obj.prmt && (
                        <div className='d-flex align-items-center justify-content-end mt-3'>
                          <div className='chatbox-user-quetext' style={{ background: widgetDetails?.client_bubble_color }}>
                            <p
                              className='outfit-regular m-0'
                              style={{ color: widgetDetails?.bubble_text_color, fontSize: `${widgetDetails?.user_bubble_font_size}px`, whiteSpace: 'pre-line' }}
                            >
                              {formatText(obj?.prmt)}
                            </p>
                          </div>
                        </div>
                      )}
                      {obj?.text && (
                        <div className='d-flex editdata-chatbox mt-3'>
                          <div
                            className='chatbox-user-replytext position-relative'
                            style={{ background: widgetDetails?.color }}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                          >
                            <div className='d-flex align-items-center mb-2'>
                              <div className='chatbox-user-icon me-2'>
                                <img src={widgetDetails?.chatbot_avtar || DEFAULT_IMAGES.SIDEBAR_BOX_IMG} alt='chatbot-icon' />
                              </div>
                              <span className='outfit-bold' style={{ color: widgetDetails?.agent_bubble_text_color }}>
                                {widgetDetails?.bot_name}
                              </span>
                            </div>
                            <p
                              className='m-0 outfit-regular'
                              style={{ color: widgetDetails?.agent_bubble_text_color, whiteSpace: 'pre-line', fontSize: `${widgetDetails?.agent_bubble_font_size}px` }}
                            >
                              <Linkify
                                componentDecorator={(decoratedHref, decoratedText, key) => (
                                  <a target='blank' href={decoratedHref} key={key}>
                                    {decoratedText}
                                  </a>
                                )}
                              >
                                {formatText(obj?.text)}
                              </Linkify>
                            </p>

                            <div className={`message-actions bot-actions ${hoveredIndex === index ? 'show' : ''}`}>
                              <button className='action-btn' onClick={() => handleCopyMessage(obj.text)} title='Copy'>
                                {copied ? <CopyCheck size={16} /> : <Copy size={16} />}
                              </button>
                              {!showForm && (
                                <button
                                  className='action-btn'
                                  onClick={() => {
                                    const userPrompt = findCorrespondingPrompt(chatData, index);
                                    handleRetryMessage(userPrompt, findPromptIndex(chatData, index));
                                  }}
                                  title='Retry'
                                  // disabled={customDisabled}
                                >
                                  <RefreshCw size={16} />
                                </button>
                              )}{' '}
                              <button
                                className='action-btn'
                                onClick={() => handleFeedback(messageId, 'positive')}
                                title='Good response'
                                style={{
                                  color: currentFeedback === 'positive' ? '#000000' : '#6B7280',
                                }}
                              >
                                <ThumbsUp size={16} fill={currentFeedback === 'positive' ? '#000000' : 'none'} />
                              </button>
                              <button
                                className='action-btn'
                                onClick={() => handleFeedback(messageId, 'negative')}
                                title='Bad response'
                                style={{
                                  color: currentFeedback === 'negative' ? '#000000' : '#6B7280',
                                }}
                              >
                                <ThumbsDown size={16} fill={currentFeedback === 'negative' ? '#000000' : 'none'} />
                              </button>
                              <span className='msg-time me-2' style={{ fontSize: '12px', color: '#999' }}>
                                | {formatTimeAgo(obj.timestamp)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })}
              {loader && (
                <>
                  <div className='custom-chat-anstext d-inline-block mt-3' style={{ background: widgetDetails?.color }}>
                    <div className='d-flex align-items-center'>
                      <div className='widget-user-icon position-relative'>
                        <img src={widgetDetails?.chatbot_avtar || DEFAULT_IMAGES.SIDEBAR_BOX_IMG} alt='chatbot-icon' />
                      </div>
                      <ThreeDotLoader />
                      <style>
                        {` 
                        .dot-flashing
                        {
                          background-color: ${widgetDetails?.client_bubble_color};
                        }
                        .dot-flashing::before{
                          background-color: ${widgetDetails?.client_bubble_color};
                         }
                        .dot-flashing::after {
                          background-color: ${widgetDetails?.client_bubble_color};
                         }
                         @keyframes dot-flashing {
                          0% {
                            background-color: ${widgetDetails?.client_bubble_color};
                          }
                          50%, 100% {
                            background-color: #5f5f5f1c;
                          }
                        }
                        `}
                      </style>
                    </div>
                  </div>
                </>
              )}

              <Recommendations
                recommendations={showRecommendation}
                widgetDetails={widgetDetails}
                onSelect={(answer) => {
                  handleChoice(0, answer);
                  setShowRecommendation(null);
                }}
              />

              {showForm && <LeadForm widgetDetails={widgetDetails} onSubmit={handleFormSubmit} disabled={isButtonDisabled} />}
              {showCalendar.show && <CalendlyEmbed url={showCalendar.link} />}
              <ScrollToBottom scrollChatData={chatData} scrollLoader={loader} showForm={showForm} showRecommendation={showRecommendation} />
            </div>
          </div>
        </div>

        {deleteConfirmPopup && <DeleteConfirmationModal widgetDetails={widgetDetails} onConfirm={handleClearData} onCancel={() => setDeleteConfirmPopup(false)} />}

        {(hideButtons || !welcomeDetails?.lead_status) && (
          <div className='p-3'>
            {showFormButton && !isLeadButtonHide && (
              <LeadButtons welcomeDetails={welcomeDetails} widgetDetails={widgetDetails} onGenerateLead={handleGenerateLead} disabled={isButtonDisabled} />
            )}

            <ChatInput
              value={values}
              onChange={(e) => setValues(e.target.value)}
              onSubmit={handleSendMessage}
              disabled={customDisabled || showForm || showCalendar.show}
              widgetDetails={widgetDetails}
            />
          </div>
        )}

        <ChatboxFooter showPrivacy={showPrivacy} welcomeDetails={welcomeDetails} widgetDetails={widgetDetails} onClosePrivacy={() => setShowPrivacy(false)} />
      </div>
      {((widgetData?.pathname === '/preview' && widgetDetails?.status && !openChatbox) || (widgetData?.pathname !== '/preview' && !openChatbox)) && (
        <>
          {welBubblePopup && (
            <div className='welcome-popup d-flex flex-column align-items-end' style={getChatbotPositionStyle(widgetDetails?.chatbot_position, isMobile)}>
              <div className={`chatbot-welcome-body align-items-end ${hideButtons && welcomeDetails?.lead_choice != null && 'p-0'}`}>
                {welcomeDetails?.welcome_msg != null && !hideButtons && (
                  <div className={`d-flex align-items-center justify-content-end ${showForm && 'mt-3'}`}>
                    {showForm && (
                      <div className='chatbox-user-icon me-3'>
                        <img src={widgetDetails?.chatbot_avtar || DEFAULT_IMAGES.SIDEBAR_BOX_IMG} alt='chatbot-icon' />
                      </div>
                    )}
                    <div
                      className={`outfit-regular welcome-text-box ${welcomeDetails?.lead_status && 'p-4'}`}
                      style={{
                        background: widgetDetails?.bubble_bg_color,
                        boxShadow: '0px 0px 15px 10px rgba(0, 0, 0, 0.1), 1px 0px 6px 5px rgba(0, 0, 0, 0.1)',
                        width: `${widgetDetails?.bubble_width}px`,
                        height: `${widgetDetails?.bubble_height}px`,
                      }}
                    >
                      <div className='bubble-close-icon' style={{ background: widgetDetails?.client_bubble_color }} onClick={() => setWelBubblePopup(false)}>
                        <IoMdClose style={{ fontSize: '20px', color: widgetDetails?.bubble_text_color }} />
                      </div>
                      <div className={`d-flex align-items-center mt-4 ${!welcomeDetails?.lead_status ? 'justify-content-between' : 'justify-content-center'}`}>
                        <div className={`${!welcomeDetails?.lead_status ? 'text-start' : 'text-center'}`} style={{ color: widgetDetails?.text_color_bubble }}>
                          <MessageComponent message={welcomeDetails?.welcome_msg} widgetDetails={widgetDetails} />
                        </div>
                        <div
                          className={`d-flex align-items-center justify-content-center ${!welcomeDetails?.lead_status ? 'cursor-pointer' : 'user-box-img p-0'}`}
                          style={{
                            width: `${widgetDetails?.bubble_avatar_size}px`,
                            height: `${widgetDetails?.bubble_avatar_size}px`,
                            minWidth: `${widgetDetails?.bubble_avatar_size}px`,
                            minHeight: `${widgetDetails?.bubble_avatar_size}px`,
                            borderRadius: '50%',
                            position: 'absolute',
                            top: 0,
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            background: widgetDetails?.bubble_bg_color,
                          }}
                          onClick={() => !welcomeDetails?.lead_status && setOpenChatbox(!openChatbox)}
                        >
                          <img
                            src={widgetDetails?.chatbot_avtar || DEFAULT_IMAGES.SIDEBAR_BOX_IMG}
                            alt='chatbot-icon'
                            style={{ width: '90%', height: 'auto', objectFit: 'cover' }}
                          />
                        </div>
                      </div>

                      {!hideButtons && welcomeDetails?.lead_choice != null && (
                        <div className='d-flex align-items-center flex-column buttonWrap gap-3'>
                          {welcomeDetails?.lead_choice.map((choice, index) => (
                            <React.Fragment key={index}>
                              <button
                                className='btn btn-submit-bubble outfit-regular'
                                style={{
                                  marginTop: index === 0 ? '1rem' : '0',
                                  color: index === 0 ? widgetDetails?.btn_primary_text : widgetDetails?.btn_secondary_text,
                                  background: index === 0 ? widgetDetails?.btn_primary_bg : widgetDetails?.btn_secondary_bg,
                                  fontSize: index === 0 ? `${widgetDetails?.user_bubble_font_size}px` : `${widgetDetails?.agent_bubble_font_size}px`,
                                  // border: `2px solid ${widgetDetails?.client_bubble_color}`,
                                  width: `${widgetDetails?.bubble_button_width}px` || '120px',
                                  height: `${widgetDetails?.bubble_button_height}px` || '40px',
                                  borderRadius: '34px',
                                }}
                                onClick={
                                  index === 0
                                    ? (e) => handleChoice(e, choice)
                                    : (e) => {
                                        handleChoice(e, choice);
                                        setShowFormButton(true);
                                      }
                                }
                              >
                                {choice}
                              </button>
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {!welBubblePopup && (
            <div className='chatbot-popup d-flex flex-column align-items-end' style={getChatbotPositionStyle(widgetDetails?.chatbot_position, isMobile)}>
              <div className='d-flex align-items-center'>
                <div className='chatbot-popup-icon' style={{ width: `${widgetDetails?.chat_bubble_icon_size}px` }} onClick={() => setOpenChatbox(!openChatbox)}>
                  <img src={widgetDetails?.launcher_icon || DEFAULT_IMAGES.DEFAULT_LAUNCHER} alt='chatbot-icon' />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {widgetData?.pathname !== '/preview' && showPublishWarning && <PublishWarningModal onClose={() => setShowPublishWarning(false)} />}
    </>
  );
};

export default React.memo(WidgetChatbot);
